import { ConnectorProfile, EntrepreneurProfileBody, InvestorProfile } from '../../types/types';
import { investorPublishedIntroSchema,
  investorPublishedInvestmentCriteriaSchema,
  entPublishedIntroSchema,
  entPublishedFinancialSummary,
  entPublishedContactInformation,
  entPublishedCompanyDetails,
  entPublishedExecSummary,
  entPublishedContactPersonPhotoSchema,
  entPublishedCompanyLogoSchema,
  connectorProfileFirstNameSchema,
  connectorProfileLastNameSchema,
  connectorProfileCompanyNameSchema,
  connectorProfileEmailSchema,
  connectorProfileSummarySchema,
  connectorProfileImageSchema,
  connectorProfileLinkedinSchema,
  connectorRefferalExpectationSchema } from '../../lib/validationSchemas';

const getPublishingGuideConfigForInvestor = (profile: InvestorProfile) => {
  const failedParts = unsatisfiedRequirementsForInvestor(profile);
  if (failedParts.length) {
    const message = 'You cannot refer deals to this investor until you complete:';
    return { publishable: false, message, status: buildInvestorProfileCompletionStatus(failedParts as InvestorProfileElements[]) };
  }

  return { publishable: true, message: 'You\'re all set to publish!', status: buildInvestorProfileCompletionStatus(failedParts) };
};

const getPublishingGuideConfigForEnt = (profile: EntrepreneurProfileBody) => {
  const failedParts = unsatisfiedRequirmentsForEnt(profile);
  if (failedParts.length) {
    const message = 'Investors will not see your profile until you complete:';
    return { publishable: false, message, status: buildEntProfileCompletionStatus(failedParts) };
  }

  return { publishable: true, message: 'You\'re all set to publish!', status: buildEntProfileCompletionStatus(failedParts) };
};

const getPublishingGuideConfigForConnector = (profile: ConnectorProfile) => {
  const failedParts = unsatisfiedRequirmentsForConnector(profile);
  if (failedParts.length) {
    const message = `To add investors or make referrals, you must provide required data for the following profile fields: ${failedParts.join(', ')}.  
    Click the pencil icons on your profile page to complete it.`;
    return { publishable: false, message, status: buildConnectoProfileCompletionStatus(failedParts as ConnectorReferralPrereqs[]) };
  }

  return { publishable: true, message: 'You\'re all set to make referrals!', status: buildConnectoProfileCompletionStatus(failedParts) };
};

export const Publishing = {
  getPublishingGuideConfigForInvestor,
  getPublishingGuideConfigForEnt,
  getPublishingGuideConfigForConnector
};

// publishing helpers
const unsatisfiedRequirementsForInvestor = (profile: InvestorProfile) => {
  const unmetReqs:InvestorProfileElements[] = [];
  // const check general info
  try {
    investorPublishedIntroSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(InvestorProfileElements.GeneralInformation);
  }

  // const check investment criteria
  try {
    investorPublishedInvestmentCriteriaSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(InvestorProfileElements.InvestmentCriteria);
  }

  return unmetReqs;
};

const unsatisfiedRequirmentsForEnt = (profile: EntrepreneurProfileBody) => {
  const unmetReqs:EntProfileElements[] = [];

  try {
    entPublishedIntroSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.GeneralInfo);
  }

  try {
    entPublishedFinancialSummary.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.FinancialSummary);
  }

  try {
    entPublishedContactInformation.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.ContactInformation);
  }

  try {
    entPublishedCompanyDetails.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.CompanyDetails);
  }

  try {
    entPublishedExecSummary.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.ExecutiveSummary);
  }

  try {
    entPublishedCompanyLogoSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.CompanyLogo);
  }

  try {
    entPublishedContactPersonPhotoSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(EntProfileElements.ContactPhoto);
  }

  return unmetReqs;
};

const unsatisfiedRequirmentsForConnector = (profile: ConnectorProfile) => {
  const unmetReqs:ConnectorReferralPrereqs[] = [];

  try {
    connectorProfileFirstNameSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.FirstName);
  }

  try {
    connectorProfileLastNameSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.LastName);
  }

  try {
    connectorProfileCompanyNameSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.CompanyName);
  }

  try {
    connectorProfileEmailSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.Email);
  }

  try {
    connectorProfileSummarySchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.Summary);
  }

  try {
    connectorProfileLinkedinSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.LinkedIn);
  }

  try {
    connectorRefferalExpectationSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.ReferralExpectation);
  }

  try {
    connectorProfileImageSchema.validateSync(profile);
  } catch (e: unknown) {
    unmetReqs.push(ConnectorReferralPrereqs.ProfilePhoto);
  }

  return unmetReqs;
};

type EntProfileCompletionStatus = Map<EntProfileElements, boolean>;
export enum EntProfileElements {
  ContactInformation = 'contact information',
  ExecutiveSummary = 'executive summary',
  CompanyDetails = 'company details',
  GeneralInfo = 'general information and files',
  FinancialSummary = 'financial summary',
  CompanyLogo = 'company logo',
  ContactPhoto = 'contact person photo'
}
const EntProfileElementsComprehension = [
  EntProfileElements.ContactInformation,
  EntProfileElements.ExecutiveSummary,
  EntProfileElements.CompanyDetails,
  EntProfileElements.GeneralInfo,
  EntProfileElements.FinancialSummary,
  EntProfileElements.CompanyLogo,
  EntProfileElements.ContactPhoto
];
const buildEntProfileCompletionStatus = (failedParts: EntProfileElements[]) => {
  const status: EntProfileCompletionStatus = new Map();
  EntProfileElementsComprehension.forEach((el) => {
    status.set(el, true);
  });
  failedParts.forEach((part) => {
    status.set(part, false);
  });
  return status;
};


type InvestorProfileCompletionStatus = Map<InvestorProfileElements, boolean>;
export enum InvestorProfileElements {
  GeneralInformation = 'general information',
  InvestmentCriteria = 'investment criteria'
}
const InvestorProfileElementsComprehension = [
  InvestorProfileElements.GeneralInformation,
  InvestorProfileElements.InvestmentCriteria
];
const buildInvestorProfileCompletionStatus = (failedParts: InvestorProfileElements[]) => {
  const status: InvestorProfileCompletionStatus = new Map();
  InvestorProfileElementsComprehension.forEach((el) => {
    status.set(el, true);
  });
  failedParts.forEach((part) => {
    status.set(part, false);
  });
  return status;
};

type ConnectorProfileCompletionStatus = Map<ConnectorReferralPrereqs, boolean>;
export enum ConnectorReferralPrereqs {
  FirstName = 'first name',
  LastName = 'last name',
  CompanyName = 'company name',
  Email = 'email',
  Summary = 'summary',
  LinkedIn = 'linkedin',
  ProfilePhoto = 'profile photo',
  ReferralExpectation = 'referral expectation'
}
const ConnectorReferralPrereqsComprehension = [
  ConnectorReferralPrereqs.FirstName,
  ConnectorReferralPrereqs.LastName,
  ConnectorReferralPrereqs.CompanyName,
  ConnectorReferralPrereqs.Email,
  ConnectorReferralPrereqs.Summary,
  ConnectorReferralPrereqs.LinkedIn,
  ConnectorReferralPrereqs.ProfilePhoto,
  ConnectorReferralPrereqs.ReferralExpectation
];

const buildConnectoProfileCompletionStatus = (failedParts: ConnectorReferralPrereqs[]) => {
  const status: ConnectorProfileCompletionStatus = new Map();
  ConnectorReferralPrereqsComprehension.forEach((el) => {
    status.set(el, true);
  });
  failedParts.forEach((part) => {
    status.set(part, false);
  });
  return status;
};

